import React, { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertHistoryListItem } from '../../components/alert-history-list-item/alert-history-list-item.component';
import { ConfirmationModal } from '../../components/confirmation-modal/confirmation-modal.component';
import { DataElement } from '../../components/data-element/data-element.component';
import { DateTimeDisplay } from '../../components/date-time-display/date-time-display.component';
import { Dropdown } from '../../components/dropdown/dropdown.component';
import { ElementWrapper } from '../../components/element-wrapper/element-wrapper.component';
import ErrorBoundary from '../../components/error-boundary/error-boundary.component';
import { LocationHistoryListItem } from '../../components/location-history-list-item/location-history-list-item.component';
import { Map } from '../../components/map/map.component';
import { PageContentWrapper } from '../../components/page-content-wrapper/page-content-wrapper.component';
import { PageHeader } from '../../components/page-header/page-header.component';
import { PaginationButtons } from '../../components/pagination-buttons/pagniation-buttons.component';
import { TabbedButtons } from '../../components/tabbed-buttons/tabbed-buttons.component';
import { UpdateIntervalModal } from '../../components/update-interval-modal/update-interval-modal.component';
import { ValidationError } from '../../components/validation-error/validation-error.component';
import { ReportDateTimeDisplay } from '../../components/report-comunicate-date-display/report-communicate-date-display.component'
import { useAccount } from '../../contexts/AccountContext';
import { useAuth } from '../../contexts/UserContext';
import { ApiService } from '../../services/api';
import { ApiError } from '../../services/api/core/ApiError';
import { Alert } from '../../services/api/models/Alert';
import { GeoLocation } from '../../services/api/models/GeoLocation';
import { SleeperReportingMode } from '../../services/api/models/SleeperReportingMode';
import { UnitDetail } from '../../services/api/models/UnitDetail';
import dayjs from 'dayjs';

interface ReportingFrequency {
    value: string;
    label: string;
    model: number;
}

type HistoryMode = 'Location History' | 'Alert History';

export const UnitOverview: FC = () => {

    const formatReportingMode = (mode: SleeperReportingMode | undefined, pending?: boolean) => {
        let str: string;

        switch (mode) {
            case 'GPSMODE10MIN': str = "GPS Mode - 10 Min"; break;
            case 'GPSMODE60MIN': str = "GPS Mode - 60 Min"; break;
            case 'SLEEPER': str = "Sleeper Mode"; break;
            default: str = 'Null'; break;
        }
        if (pending) {
            return 'PENDING: ' + str;
        } else {
            return str;
        }
    }

    const intervals: ReportingFrequency[] = [
        {
            value: 'GPSMODE10MIN',
            label: formatReportingMode('GPSMODE10MIN'),
            model:1,
        },
        {
            value: 'GPSMODE60MIN',
            label: formatReportingMode('GPSMODE60MIN'),
            model:1,
        },
        {
            value: 'SLEEPER',
            label: 'Sleeper Mode',
            model:1,
        },
        {
            value: 'SLEEPERDAILY',
            label: 'Sleeper Mode Daily',
            model:1,
        }
    ];


    const navigate = useNavigate();

    const [unit, setUnit] = useState<UnitDetail | null>();
    const [historyMode, setHistoryMode] = useState<HistoryMode>('Location History');
    const [updateModalOpen, setUpdateModalOpen] = useState<boolean>(false);
    const [confirmUpdateModalOpen, setConfirmUpdateModalOpen] = useState<boolean>(false);
    const [queuedUpdate, setQueuedUpdate] = useState<SleeperReportingMode>();
    const [locationHistory, setLocationHistory] = useState<GeoLocation[] | null>();
    const [alertHistory, setAlertHistory] = useState<Alert[]>([]);
   

    const [page, setPage] = useState<number>(parseInt(localStorage.getItem("currentPage") || "0"));

    const [currentPage, setCurrentPage] = useState(parseInt(localStorage.getItem("currentPage") || "0"));

    useEffect(() => {
        localStorage.setItem("currentPage", currentPage.toString());
    }, [page, currentPage]);

    const [pageLength, setPageLength] = useState<number>(0);

    const { unitId } = useParams();

    const { logout } = useAuth();
    const { refreshUnits, units } = useAccount();

    const refreshUnit = async () => {
        await refreshUnits();
        getUnitFromStore();
    }

    const getUnitFromStore = async () => {
        const response = await ApiService.getUnit(Number(unitId));
        setUnit(response);
    }

    useEffect(() => {
        if (unitId) {
            getUnitFromStore();
        }
    }, [units, unitId]);

    useEffect(() => {
        if (unit) {
            fetchLocationHistory();
            fetchAlertHistory();
        }
    }, [unit]);


    const updateReportingInterval = (s: SleeperReportingMode) => {
        setQueuedUpdate(s);
        setConfirmUpdateModalOpen(true);
    };

    useEffect(() => {
        if (queuedUpdate) {
            setConfirmUpdateModalOpen(true);
        }
    }, [queuedUpdate])

    const postQueuedUpdate = async () => {
        setConfirmUpdateModalOpen(false);
        if (!queuedUpdate) {
            return;
        }
        const success = await ApiService.toggleGpsMode(Number(unitId), queuedUpdate);
        if (success) {
            setUpdateModalOpen(true);
            await refreshUnit();
        } else {

        }
    }

    const clearQueuedUpdate = () => {
        setQueuedUpdate(undefined);
        setConfirmUpdateModalOpen(false);
    }

    const fetchLocationHistory = async () => {
        if (!unit) return;
        try {
            const locations = await ApiService.getLocationHistory(Number(unit.id));
            setLocationHistory(locations);
        } catch (e) {
            if (e instanceof ApiError && (e.status === 400 || e.status === 401 || e.status === 403)) {
                logout();
            }
        }
    };

    const onHandlePageChange = (pageNumber: any) => {
        setCurrentPage(pageNumber);
        setPage(pageNumber);
    };

    const fetchAlertHistory = async () => {
        if (!unit) return;
        try {
            const response = await ApiService.getAlertHistory(Number(unit.id));
            setAlertHistory(response);
        } catch (e) {
            if (e instanceof ApiError && (e.status === 400 || e.status === 401 || e.status === 403)) {
                logout();
            }
        }
    }

    useEffect(() => {
       
        if (historyMode === 'Location History') {
            setPage(currentPage);
            if (locationHistory) {
                setPageLength(Math.ceil(locationHistory.length / 10 - 1 || 0));
                return;
            }
        } else {
            setPage(0);
            setCurrentPage(0);
            if (alertHistory) {
                setPageLength(Math.ceil(alertHistory.length / 10 - 1 || 0));
                return;
            }
        }
        setPageLength(0);
    }, [historyMode, alertHistory, locationHistory])


    return (
        <>
            <div className="flex flex-1 flex-col relative">
                <PageHeader title="Asset Details" showBack />
                <PageContentWrapper>
                    {
                        unit === undefined
                            ?
                            <h2>Loading...</h2>
                            :
                            unit === null
                                ?
                                <ValidationError>Unit not found</ValidationError>
                                :
                                <>
                                    <div className="flex flex-col gap-2">
                                        <h3 className="font-bold mb-3">{unit.simNumber} - {unit.model}</h3>
                                        <div className="flex gap-1 sm:gap-2 md:gap-4">
                                            <DataElement title="Unique ID" body={String(unit.imei)} />
                                            <DataElement title="Registration" body={String(unit.registration == null ? "" : unit.registration)} />
                                        </div>
                                        <div className="flex gap-1 sm:gap-2 md:gap-4">
                                            <DataElement title="Last Communicated" body={String(dayjs(unit.lastCommunication).format('DD/MM/YY HH:mm:ss') )} />
                                            <DataElement title="Last Location Time" body={unit.lastUpdate ?  String(dayjs(unit.lastUpdate).format('DD/MM/YY HH:mm:ss')) : "" } />
                                        </div>
                                        <div className="flex gap-1 sm:gap-2 md:gap-4">
                                            <DataElement title="Remaining Updates" body={String(unit.remainingUpdates)} />
                                            <DataElement title="Current Active Setting" body={formatReportingMode(unit.currentCommunicationMode)} />
                                        </div>

                                        <ElementWrapper title="Next Communication Estimate">
                                            <DateTimeDisplay isoDate={unit.nextUpdate || ''} enlarge />
                                        </ElementWrapper>

                                        {
                                            unit.lastKnownLocation && unit.currentCommunicationMode && (
                                                <div className='cursor-pointer' onMouseDown={() => { navigate(`location/${0}`); }}>
                                                    <ErrorBoundary>
                                                        <Map location={{ lat: unit.lastKnownLocation.latitude || 0, lng: unit.lastKnownLocation.longitude || 0 }} radius={unit.lastKnownLocation.confidenceRadius} disableInteraction height={400} type={unit.lastKnownLocation.locationType === 'CELL' ? 'SLEEPER' : 'GPS'} />
                                                    </ErrorBoundary>
                                                </div>
                                            )
                                        }



                                        <div className="w-full bg- p-5 rounded-lg flex justify-center items-center trakDarkGradient">
                                            <p className="text-xl text-white text-center">
                                                {
                                                    unit.pendingCommunicationMode
                                                        ?
                                                        formatReportingMode(unit.pendingCommunicationMode, true)
                                                        :
                                                        formatReportingMode(unit.currentCommunicationMode)
                                                }
                                            </p>
                                        </div>
                                        <Dropdown
                                            values={intervals.map(i => {
                                                return { value: i.value, label: i.label };
                                            })}
                                            onSelect={s => updateReportingInterval(s.value)}
                                            initialValue={intervals.filter(f => f.value === unit.pendingCommunicationMode || unit.currentCommunicationMode)}
                                            label="Change Update Setting"
                                        />

                                        <div className="flex flex-col w-full rounded-t-md border border-b-0 border-gray-300 overflow-hidden">
                                            <TabbedButtons options={['Location History', 'Alert History']} mode={historyMode} select={s => setHistoryMode(s as HistoryMode)} />
                                            <div>
                                                {
                                                    historyMode === 'Location History'
                                                        ?
                                                        locationHistory && locationHistory.length > 0
                                                        &&
                                                        locationHistory.slice(10 * page, 10 * page + 10).map(
                                                            (l, i) =>
                                                                l && (
                                                                    <LocationHistoryListItem
                                                                        location={l}
                                                                        key={i}
                                                                        onSelect={() => {
                                                                            navigate(`location/${10 * page + i}`);
                                                                        }}
                                                                    />
                                                                ),
                                                        )
                                                        :
                                                        alertHistory && alertHistory.length > 0
                                                        &&
                                                        alertHistory.slice(10 * page, 10 * page + 10).map((a, i) => <AlertHistoryListItem alert={a} key={i} onSelect={() => { navigate(`event/${10 * page + i}`) }} />)
                                                }
                                            </div>
                                        </div>

                                        {
                                            historyMode === 'Location History'
                                                ?
                                                !locationHistory || locationHistory?.length === 0 && <h3 className='text-center my-3'>No locations found</h3>
                                                :
                                                !alertHistory || alertHistory.length === 0 && <h3 className='text-center my-3'>No alerts found</h3>
                                        }



                                    </div>
                                    {
                                        pageLength > 1 && <PaginationButtons numPages={pageLength} selected={page} onPress={onHandlePageChange} startingPage={0} />
                                    }
                                </>
                    }
                </PageContentWrapper>
                {updateModalOpen && <UpdateIntervalModal onAcknowledged={() => setUpdateModalOpen(false)} />}
                {
                    confirmUpdateModalOpen
                    &&
                    <div className=' absolute top-0 left-0 bottom-0 right-0 '>
                        <ConfirmationModal confirm={postQueuedUpdate} cancel={clearQueuedUpdate}>Do you wish to update the reporting interval time on this device?</ConfirmationModal>
                    </div>
                }
            </div>
        </>
    );
};
